import { useRef, useState, useCallback } from 'react';

import { getCountdownSeconds, getExpectedSentAtFromSubmittedAt } from 'utils/report';
import { SubmittedAtTime } from './types';
import {
  worklistUpdateBc,
  WorklistRefetchReason,
} from 'modules/EventsManager/worklistUpdateChannel';

type UseCountdownOpts = {
  worklistItemSmid: string;
  onStart?: () => void;
  onCancel?: () => void;
};

type UseCountdownReturn = {
  cancelCountdown: () => Promise<void>;
  clearTimer: () => void;
  countdownSeconds: null | number;
  setCountdownSeconds: (arg1: ((arg1: null | number) => null | number) | null | number) => void;
  startCountdown: (v?: {
    submittedAt?: SubmittedAtTime | null;
    expectedSentAt?: SubmittedAtTime | null;
  }) => Promise<void>;
};

export const useCountdown = ({
  worklistItemSmid,
  onStart = () => {},
  onCancel = () => {},
}: UseCountdownOpts): UseCountdownReturn => {
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const clearTimer = useCallback(() => {
    setCountdownSeconds(null);
    clearInterval(countdownIntervalRef.current);
  }, []);

  const changeTime = useCallback((expectedSentAt: SubmittedAtTime) => {
    const countdownSeconds = getCountdownSeconds(expectedSentAt);
    setCountdownSeconds(countdownSeconds);
  }, []);

  const startCountdown = useCallback(
    async ({
      submittedAt,
      expectedSentAt,
    }: {
      submittedAt?: SubmittedAtTime | null;
      expectedSentAt?: SubmittedAtTime | null;
    } = {}) => {
      clearTimer();
      onStart();

      const expectedSentAtWithFallback = (() => {
        if (expectedSentAt != null) return expectedSentAt;

        if (submittedAt != null) {
          return getExpectedSentAtFromSubmittedAt(submittedAt) ?? new Date();
        }

        return getExpectedSentAtFromSubmittedAt(new Date()) ?? new Date();
      })();
      changeTime(expectedSentAtWithFallback);

      // interval every 500ms to prevent edge cases to do with rounding errors with timestamp-differences
      countdownIntervalRef.current = setInterval(() => changeTime(expectedSentAtWithFallback), 500);

      worklistUpdateBc.postMessage({
        smid: worklistItemSmid,
        reason: WorklistRefetchReason.Countdown,
      });
    },
    [worklistItemSmid, onStart, clearTimer, changeTime]
  );

  const cancelCountdown = useCallback(async () => {
    clearTimer();
    onCancel();

    worklistUpdateBc.postMessage({
      smid: worklistItemSmid,
      reason: WorklistRefetchReason.Countdown,
    });
  }, [clearTimer, onCancel, worklistItemSmid]);

  return {
    countdownSeconds,
    setCountdownSeconds,
    startCountdown,
    cancelCountdown,
    clearTimer,
  };
};
